/**
 * Actiebalk onderaan een pane, naar het patroon van Snap: groepen van icoonknoppen
 * met een onderschrift, een groepslabel eronder, scheidingen tussen de groepen, en
 * per knop een menuvenster (popover) dat boven de balk opent.
 *
 * Gebruik:
 *   <ActionBar popover={<ActionPopover anchorRef={ref} open={...} onClose={...}>…</ActionPopover>}>
 *     <ActionBarGroup label="Bron"><ActionButton ref={ref} icon={…} caption="Bron" state="done" /></ActionBarGroup>
 *   </ActionBar>
 *
 * Smalle panes (grenzen in barBreakpoints.ts): de onderschriften staan sinds 16 oktober 2026 altijd uit
 * (naam in tooltip en toegankelijke naam); onder 340 px ("tight") verdwijnen de groepslabels en krijgen de
 * knoppen de kleine maat, daarboven maat M (48 × 40, icoon 24; besluit N2, 20 oktober 2026). De balk blijft
 * één regel; hij stapelt niet.
 *
 * Namen (codex-review GEM-03, 27 september 2026): het onderschrift zegt wat er in het menu staat
 * of gebeurt (Bron, Facturen, Analyse, Dekking …), niet een fase. Een badge heeft altijd een
 * eenheid in `badgeLabel` ("54 tabellen herkend"): die staat in tooltip en toegankelijke naam, zodat
 * ook zonder onderschrift duidelijk is wat het getal telt.
 */

import * as React from "react";
import { HelpArea } from "./HelpArea";
import { compactEurosInText } from "./compactAmount";
import { shellText } from "./shellText";
import { useLang } from "../../i18n/LangContext";
import type { Lang } from "../../i18n/language";
import "./pane.css";

import type { ActionState, StepMark } from "./nextStep";
import { actionBarDensity, type ActionBarDensity } from "./barBreakpoints";
import { planTabBar } from "./tabBarRules";
import { PANE_MENU_READABLE_MAX, popoverPlacement } from "./menuPlacement";

// De staat van een stap staat in nextStep.ts (zuiver, ook voor de engine-tests).
export type { ActionState };

const STATE_WORDS: Record<Lang, Record<ActionState, string>> = {
  NL: {
    todo: "nog niet",
    ready: "gereed om te starten",
    active: "bezig",
    done: "klaar",
    stale: "opnieuw nodig",
    error: "let op",
  },
  EN: {
    todo: "not yet",
    ready: "ready to start",
    active: "working",
    done: "done",
    stale: "needs redoing",
    error: "attention",
  },
};
/** De staat in woorden, voor tooltip, toegankelijke naam en de kop van het menu. */
export const actionStateWord = (s: ActionState, lang: Lang = "NL"): string =>
  STATE_WORDS[lang === "EN" ? "EN" : "NL"][s];

/** Kop van de statusregel in een menu. */
const STATUS_HEAD: Record<Lang, Record<ActionState, string>> = {
  NL: {
    todo: "Nog te doen",
    ready: "Gereed om te starten",
    active: "Bezig",
    done: "✓ Gereed",
    stale: "Opnieuw nodig",
    error: "Let op",
  },
  EN: {
    todo: "To do",
    ready: "Ready to start",
    active: "Working",
    done: "✓ Done",
    stale: "Needs redoing",
    error: "Attention",
  },
};

// ---------------------------------------------------------------- balk ----

interface ActionBarProps {
  children: React.ReactNode;
  /** Het open menuvenster; wordt in de balk gerenderd zodat het erboven kan staan. */
  popover?: React.ReactNode;
  /** Optioneel: dunne strook boven de balk (status, voortgang). */
  strip?: React.ReactNode;
  /** Optioneel: de regel "Volgende stap" (NextStepLine), direct boven de knoppen. */
  next?: React.ReactNode;
  className?: string;
}

/** Breedte van een groep zonder krimp: de flexrij kan een groep smaller maken dan zijn knoppen. */
const naturalWidth = (el: HTMLElement): number =>
  Math.max(el.getBoundingClientRect().width, el.scrollWidth);

const BarContext = React.createContext<React.RefObject<HTMLDivElement> | null>(null);

/**
 * Inklappen van groepen naast een Tab-groep (besluiten D2/D3 en Q2, 20 oktober 2026; D4/D5, 27 oktober 2026).
 * De balk meet de groepen en bepaalt met planTabBar (tabBarRules.ts) welke groepen ingeklapt worden: alleen
 * als ze samen niet in de rij passen. Een groep leest hier of hij ingeklapt is en meldt zijn element aan voor
 * de meting. Zonder Tab-groep gebeurt er niets.
 */
interface CollapseApi {
  collapsed: ReadonlySet<string>;
  register: (id: string, el: HTMLElement | null, collapsible: boolean) => void;
  tab: React.MutableRefObject<HTMLElement | null>;
  density: ActionBarDensity;
}
const CollapseContext = React.createContext<CollapseApi | null>(null);

export function ActionBar({
  children,
  popover,
  strip,
  next,
  className,
}: ActionBarProps): JSX.Element {
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const rowRef = React.useRef<HTMLDivElement>(null);
  const [density, setDensity] = React.useState<ActionBarDensity>("full");
  const [collapsed, setCollapsed] = React.useState<ReadonlySet<string>>(() => new Set());
  const groups = React.useRef(new Map<string, { el: HTMLElement; collapsible: boolean }>());
  // Gemeten breedtes per groep, uitgeklapt en ingeklapt; blijven bewaard zodat de planning niet heen en
  // weer springt (een groep wordt pas gemeten in de stand waarin hij staat).
  const widths = React.useRef(new Map<string, { full?: number; mini?: number }>());
  const tab = React.useRef<HTMLElement | null>(null);
  const collapsedRef = React.useRef(collapsed);
  collapsedRef.current = collapsed;

  const replan = React.useCallback((): void => {
    const row = rowRef.current;
    const tabEl = tab.current;
    if (!row || !tabEl) return;
    const rowStyle = window.getComputedStyle(row);
    const gap = parseFloat(rowStyle.columnGap || rowStyle.gap || "4") || 4;
    const available =
      row.clientWidth -
      (parseFloat(rowStyle.paddingLeft) || 0) -
      (parseFloat(rowStyle.paddingRight) || 0);
    const specs: { id: string; fullWidth: number; miniWidth: number; buttons: number }[] = [];
    const btnW = density === "tight" ? 36 : 48;
    groups.current.forEach(({ el, collapsible }, id) => {
      const w = widths.current.get(id) ?? {};
      // + tussenruimte en marge van de scheiding; scrollWidth telt ook als de flexrij de groep al smaller maakte.
      // De stand komt van het element zelf: een groep die niet (meer) mag inklappen staat altijd uitgeklapt.
      const cs = window.getComputedStyle(el);
      const margin = parseFloat(cs.marginLeft) || 0;
      const measured = naturalWidth(el) + gap + margin;
      if (el.classList.contains("is-collapsed")) w.mini = measured;
      else w.full = measured;
      widths.current.set(id, w);
      const mini =
        w.mini ??
        btnW +
          (parseFloat(cs.paddingLeft) || 0) +
          (parseFloat(cs.borderLeftWidth) || 0) +
          gap +
          margin;
      specs.push({
        id,
        fullWidth: w.full ?? measured,
        miniWidth: mini,
        buttons: collapsible ? 2 : 1,
      });
    });
    // De Tab-groep is één icoonknop met een vaste breedte (D4); hij staat vooraan, dus zonder tussenruimte.
    const plan = planTabBar(available, naturalWidth(tabEl), specs);
    const next = new Set(plan.collapsed);
    const cur = collapsedRef.current;
    if (next.size !== cur.size || Array.from(next).some((id) => !cur.has(id))) setCollapsed(next);
  }, [density]);

  React.useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    // Grenzen op één plek (N2): barBreakpoints.ts.
    const apply = (w: number): void => setDensity(actionBarDensity(w));
    apply(el.clientWidth);
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) apply(e.contentRect.width);
      replan();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [replan]);
  // Na elke render opnieuw plannen (andere tabnaam, andere knoppen); setCollapsed alleen bij verschil.
  React.useLayoutEffect(() => {
    replan();
  });

  const api = React.useMemo<CollapseApi>(
    () => ({
      collapsed,
      register: (id, el, collapsible) => {
        if (el) groups.current.set(id, { el, collapsible });
        else groups.current.delete(id);
      },
      tab,
      density,
    }),
    [collapsed, density]
  );

  return (
    <BarContext.Provider value={wrapRef}>
      <CollapseContext.Provider value={api}>
        <div
          ref={wrapRef}
          className={`pane-actionbar${className ? ` ${className}` : ""}`}
          data-density={density}
        >
          {popover}
          {strip ? <div className="pane-actionbar__strip">{strip}</div> : null}
          {next ?? null}
          <div ref={rowRef} className="pane-actionbar__row" role="toolbar">
            {children}
          </div>
        </div>
      </CollapseContext.Provider>
    </BarContext.Provider>
  );
}

// -------------------------------------------------------------- groep ----

export interface ActionBarGroupProps {
  label?: string;
  children: React.ReactNode;
  /**
   * Vaste naam ("run", "follow", "filter") voor het inklappen naast een Tab-groep (D3). Met `collapsible`
   * wordt de groep bij plaatsgebrek één knop met ▾ die een menu met zijn knoppen opent; `mini` beschrijft die
   * knop (icoon, tooltip, badge en staat van de belangrijkste knop).
   */
  id?: string;
  collapsible?: boolean;
  mini?: {
    icon: React.ReactNode;
    title?: string;
    badge?: string | number;
    badgeLabel?: string;
    state?: ActionState;
    step?: StepMark;
    dot?: boolean;
  };
}

export function ActionBarGroup({
  label,
  children,
  id,
  collapsible,
  mini,
}: ActionBarGroupProps): JSX.Element {
  const api = React.useContext(CollapseContext);
  const lang = useLang();
  const canCollapse = !!id && !!collapsible && !!mini;
  const isCollapsed = canCollapse && !!api && api.collapsed.has(id as string);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const miniRef = React.useRef<HTMLButtonElement>(null);
  const elRef = React.useCallback(
    (el: HTMLDivElement | null) => {
      if (id && api) api.register(id, el, canCollapse);
    },
    [id, api, canCollapse]
  );
  // Ingeklapt: de knoppen van de groep als menuregels; hun refs wijzen naar de ingeklapte knop, zodat
  // een menu dat op zo'n knop was verankerd boven de ingeklapte knop opent.
  const items = React.Children.toArray(children).filter(React.isValidElement) as React.ReactElement<
    ActionButtonProps & { ref?: React.MutableRefObject<HTMLButtonElement | null> }
  >[];
  React.useLayoutEffect(() => {
    if (!isCollapsed) return;
    items.forEach((child) => {
      const r = (child as unknown as { ref?: React.MutableRefObject<HTMLButtonElement | null> })
        .ref;
      if (r && typeof r === "object") r.current = miniRef.current;
    });
  });
  React.useEffect(() => {
    if (!isCollapsed) setMenuOpen(false);
  }, [isCollapsed]);

  return (
    <div
      ref={elRef}
      className={`pane-actionbar__group${isCollapsed ? " is-collapsed" : ""}`}
      role="group"
      aria-label={label}
    >
      {isCollapsed && mini ? (
        <div className="pane-actionbar__buttons">
          <ActionButton
            ref={miniRef}
            icon={mini.icon}
            caption={label ?? ""}
            title={mini.title ?? label}
            badge={mini.badge}
            badgeLabel={mini.badgeLabel}
            state={mini.state}
            step={mini.step}
            dot={mini.dot}
            more
            open={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          />
          <ActionPopover
            anchorRef={miniRef}
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            title={label}
            plain
          >
            {items.map((child, i) => {
              const p = child.props;
              const word =
                p.state && p.state !== "todo"
                  ? (p.stateLabel ?? actionStateWord(p.state, lang))
                  : null;
              const aside = [p.badgeLabel ?? (p.badge !== undefined ? String(p.badge) : null), word]
                .filter(Boolean)
                .join(" · ");
              return (
                <button
                  key={i}
                  type="button"
                  className={`pane-menu__item${p.step === "current" || p.pressed ? " is-selected" : ""}`}
                  aria-pressed={p.pressed}
                  disabled={p.disabled}
                  title={p.title ?? p.caption}
                  onClick={() => {
                    setMenuOpen(false);
                    p.onClick?.();
                  }}
                >
                  <span className="pane-menu__item-icon">{p.icon}</span>
                  <span className="pane-menu__item-text">{p.caption}</span>
                  {aside ? <span className="pane-menu__item-aside">{aside}</span> : null}
                </button>
              );
            })}
          </ActionPopover>
        </div>
      ) : (
        <div className="pane-actionbar__buttons">{children}</div>
      )}
      {label ? <div className="pane-actionbar__label">{label}</div> : null}
    </div>
  );
}

// ------------------------------------------------------------ Tab-groep ----

export interface ActionBarTabProps {
  /** Naam van het gekozen tabblad (of "Summary", "Alle vragen"). */
  name: string;
  /** Aantal, bijvoorbeeld "2/4". */
  count?: string;
  /** Groepslabel, standaard "Tab". */
  label: string;
  /**
   * Toegankelijke naam en begin van de tooltip, bijvoorbeeld "Tabblad: 1. Financial (2/4)"; standaard de naam
   * met het aantal tussen haakjes.
   */
  tip?: string;
  /** Extra uitleg achter de tooltip ("kiezen opent ook het blad in Excel"). */
  title?: string;
  /** Icoon van de knop (step/tabs). */
  icon: React.ReactNode;
  open?: boolean;
  onClick?: () => void;
  buttonRef?: React.RefObject<HTMLButtonElement>;
}

/**
 * De groep Tab links in de balk: sinds 27 oktober 2026 (D4, #2831) één icoonknop zoals de andere knoppen;
 * tabnaam en aantal staan in tooltip en toegankelijke naam, het menu blijft zoals het was.
 */
export function ActionBarTab({
  name,
  count,
  label,
  tip,
  title,
  icon,
  open,
  onClick,
  buttonRef,
}: ActionBarTabProps): JSX.Element {
  const api = React.useContext(CollapseContext);
  const elRef = React.useCallback(
    (el: HTMLDivElement | null) => {
      if (api) api.tab.current = el;
    },
    [api]
  );
  const caption = tip ?? `${name}${count ? ` (${count})` : ""}`;
  return (
    <div ref={elRef} className="pane-actionbar__group is-tab" role="group" aria-label={label}>
      <div className="pane-actionbar__buttons">
        <ActionButton
          ref={buttonRef}
          icon={icon}
          caption={caption}
          title={title ? `${caption} — ${title}` : caption}
          open={open}
          onClick={onClick}
        />
      </div>
      <div className="pane-actionbar__label">{label}</div>
    </div>
  );
}

// --------------------------------------------------------------- knop ----

export interface ActionButtonProps {
  icon: React.ReactNode;
  caption: string;
  title?: string;
  /** Staat van de stap: een gekleurd puntje op de knop. */
  state?: ActionState;
  /** Eigen woord voor de staat (bijvoorbeeld "nog niet bijgewerkt"); anders actionStateWord. */
  stateLabel?: string;
  /** Het menu van deze knop staat open. */
  open?: boolean;
  /**
   * Plaats in de workflow (besluit Asil, 2 oktober 2026; zie stepMarks): "current" = de stap waar de regel
   * Volgende stap naar wijst (accentrand, vet onderschrift); "later" = komt daarna en is nog niet klaar
   * (gedempt, wel klikbaar). Los van `open`.
   */
  step?: StepMark;
  disabled?: boolean;
  /** Klein getal of tekst rechtsboven, bijvoorbeeld het aantal facturen. */
  badge?: string | number;
  /** Het getal met eenheid ("12 facturen uitgelezen"), voor tooltip en toegankelijke naam. */
  badgeLabel?: string;
  /** Aan-stand van een filterknop (donker vlak, aria-pressed). */
  pressed?: boolean;
  /** Kleine stip rechtsboven: er staat een filter aan (Q3). */
  dot?: boolean;
  /** Ingeklapte groep: een klein ▾ rechtsonder. */
  more?: boolean;
  onClick?: () => void;
}

export const ActionButton = React.forwardRef<HTMLButtonElement, ActionButtonProps>(
  function ActionButton(
    {
      icon,
      caption,
      title,
      state,
      stateLabel,
      open,
      step,
      disabled,
      badge,
      badgeLabel,
      pressed,
      dot,
      more,
      onClick,
    },
    ref
  ) {
    const lang = useLang();
    const nextWord = shellText("nextStep", lang);
    const word = state && state !== "todo" ? (stateLabel ?? actionStateWord(state, lang)) : null;
    const hasBadge = badge !== undefined && badge !== "";
    const count = hasBadge ? (badgeLabel ?? String(badge)) : null;
    const base = title ?? caption;
    // Het getal met eenheid alleen toevoegen als de tooltip het nog niet noemt.
    const tip = [
      base,
      count && !base.includes(count) ? count : null,
      word,
      step === "current" ? nextWord : null,
    ]
      .filter(Boolean)
      .join(" · ");
    return (
      <button
        ref={ref}
        type="button"
        className={`pane-actionbar__btn${open ? " is-open" : ""}${step ? ` is-${step}` : ""}${pressed ? " is-pressed" : ""}`}
        title={tip}
        aria-label={[caption, count, word, step === "current" ? nextWord : null]
          .filter(Boolean)
          .join(", ")}
        aria-current={step === "current" ? "step" : undefined}
        aria-expanded={open}
        aria-pressed={pressed}
        aria-haspopup={onClick && pressed === undefined ? "dialog" : undefined}
        disabled={disabled}
        onClick={onClick}
        data-state={state}
      >
        <span className="pane-actionbar__icon">{icon}</span>
        <span className="pane-actionbar__caption">{caption}</span>
        {dot ? <span className="pane-actionbar__fdot" aria-hidden="true" /> : null}
        {more ? (
          <span className="pane-actionbar__more" aria-hidden="true">
            ▾
          </span>
        ) : null}
        {/* Klaar = een duidelijk vinkje op de knop (besluit Asil, 1 oktober 2026); andere staten een gekleurd puntje.
          Met een aantal één label: "✓ 60" in groen als de stap klaar is, anders het aantal in de kleur van de
          staat; vinkje en aantal stonden eerst over elkaar heen (feedback Asil 2 oktober). */}
        {hasBadge ? (
          <span className={`pane-actionbar__badge is-${state ?? "todo"}`} aria-hidden="true">
            {state === "done" ? "✓ " : ""}
            {badge}
          </span>
        ) : state === "done" ? (
          <span className="pane-actionbar__check" aria-hidden="true">
            ✓
          </span>
        ) : state && state !== "todo" ? (
          <span className={`pane-actionbar__dot is-${state}`} aria-hidden="true" />
        ) : null}
      </button>
    );
  }
);

// ------------------------------------------------------------ popover ----

interface ActionPopoverProps {
  anchorRef: React.RefObject<HTMLElement>;
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  /** Rechts in de kop, bijvoorbeeld een kleine link. Een staat hoort in `status`. */
  aside?: React.ReactNode;
  /**
   * Staat van de stap als volle regel bovenaan het venster (besluit Asil, 1 oktober 2026: het
   * woordje naast het kruisje viel niet op). `text` zegt wat er klaar is of wat er nog ontbreekt.
   */
  status?: { state: ActionState; text?: React.ReactNode; head?: string } | null;
  /**
   * Lijstmenu: smalle binnenmarge, de regels (pane-menu__item) vullen de breedte. Sinds #2826 (25 oktober 2026)
   * vult ook een lijstmenu de breedte van de pane, net als elk ander menuvenster.
   */
  plain?: boolean;
  children: React.ReactNode;
}

/** Leesbare maximale breedte van een menuvenster (W2, #2730); daaronder vult het de pane met 16 px marge. */
export const POPOVER_READABLE_MAX = PANE_MENU_READABLE_MAX;
/** Het menuvenster groeit tot 85% van de hoogte van het venster (W2), en nooit boven de ruimte boven de balk. */
const POPOVER_MAX_HEIGHT_SHARE = 0.85;

/**
 * Menuvenster boven de balk. Elk venster, ook een lijstmenu (`plain`, #2826), vult de breedte van de pane met
 * 16 px marge per kant, tot POPOVER_READABLE_MAX (gecentreerd). De hoogte groeit tot 85% van het venster (feedback
 * tester, W2/W3, 23 oktober 2026). Sluit met Esc of een klik buiten het venster.
 *
 * Focus (review §14, 4 oktober 2026): bij openen naar het eerste nog in te vullen veld
 * (aria-invalid) of anders de titel; bij sluiten terug naar de knop die het venster opende,
 * tenzij de gebruiker intussen elders heeft geklikt. De titel benoemt het venster (aria-labelledby).
 */
export function ActionPopover({
  anchorRef,
  open,
  onClose,
  title,
  aside,
  status,
  plain,
  children,
}: ActionPopoverProps): JSX.Element | null {
  const barRef = React.useContext(BarContext);
  const lang = useLang();
  const boxRef = React.useRef<HTMLDivElement>(null);
  const titleRef = React.useRef<HTMLSpanElement>(null);
  const titleId = React.useId();
  const [left, setLeft] = React.useState<number>(8);
  const [w, setW] = React.useState<number>(POPOVER_READABLE_MAX);
  const [maxH, setMaxH] = React.useState<number | null>(null);

  // Breedte en plaats bij openen én bij elke andere breedte van de pane (BUG-03, 1 oktober 2026: een open
  // menu bleef na verkleinen op de oude breedte, met het sluitkruisje buiten beeld).
  React.useLayoutEffect(() => {
    if (!open) return undefined;
    const bar = barRef?.current;
    if (!bar) return undefined;
    const place = (): void => {
      if (!anchorRef.current) return;
      const barRect = bar.getBoundingClientRect();
      const { left: l, width: ww } = popoverPlacement(barRect.width);
      setLeft(l);
      setW(ww);
      // Ruimte boven de balk: het venster staat 6 px boven de balk en houdt 8 px vrij aan de bovenkant.
      const room = barRect.top - 14;
      setMaxH(Math.max(200, Math.min(window.innerHeight * POPOVER_MAX_HEIGHT_SHARE, room)));
    };
    place();
    window.addEventListener("resize", place);
    const Observer = (window as unknown as { ResizeObserver?: typeof ResizeObserver })
      .ResizeObserver;
    const ro = Observer ? new Observer(place) : null;
    ro?.observe(bar);
    return () => {
      window.removeEventListener("resize", place);
      ro?.disconnect();
    };
  }, [open, anchorRef, barRef]);

  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    const onDown = (e: MouseEvent): void => {
      const t = e.target as Node;
      if (boxRef.current?.contains(t)) return;
      if (anchorRef.current?.contains(t)) return;
      onClose();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open, onClose, anchorRef]);

  React.useEffect(() => {
    if (!open) return undefined;
    const box = boxRef.current;
    const anchor = anchorRef.current;
    const field = box?.querySelector<HTMLElement>('[aria-invalid="true"]:not([disabled])');
    const target =
      field ?? (titleRef.current && titleRef.current.textContent ? titleRef.current : box);
    target?.focus({ preventScroll: true });
    return () => {
      // Na het sluiten (venster weg) of een ander menu: terug naar de knop, alleen als de focus in het venster
      // stond of verdwenen is; een klik op een ander element houdt zijn focus.
      const active = document.activeElement;
      if (anchor && (!active || active === document.body || (!!box && box.contains(active))))
        anchor.focus({ preventScroll: true });
    };
  }, [open, anchorRef]);

  if (!open) return null;
  // "todo" alleen met tekst (de volgende stap); `head` vervangt het staatwoord.
  const showStatus = !!status && (status.state !== "todo" || !!status.text);
  const stateHead = status
    ? (status.head ?? STATUS_HEAD[lang === "EN" ? "EN" : "NL"][status.state])
    : "";
  const statusText =
    status && typeof status.text === "string" ? compactEurosInText(status.text) : status?.text;
  const statusTitle = status
    ? [stateHead, typeof status.text === "string" ? status.text : ""].filter(Boolean).join(" — ")
    : undefined;
  return (
    <div
      ref={boxRef}
      className="pane-popover"
      role="dialog"
      aria-modal="false"
      aria-labelledby={title ? titleId : undefined}
      tabIndex={-1}
      style={
        {
          left,
          width: w,
          ...(maxH ? { maxHeight: maxH, "--pane-popover-maxh": `${Math.round(maxH)}px` } : {}),
        } as React.CSSProperties
      }
    >
      {(title || aside || showStatus) && (
        <div className="pane-popover__head">
          <span ref={titleRef} id={titleId} className="pane-popover__title" tabIndex={-1}>
            {title}
          </span>
          {/* Status compact naast de titel (feedback Asil 2 oktober 2026): staatwoord in kleur, geen achtergrond;
              bedragen kort ("€157k"); de volledige tekst staat in de tooltip. */}
          {showStatus && status ? (
            <span
              className={`pane-popover__state is-${status.state}`}
              role="status"
              title={statusTitle}
            >
              {stateHead ? <strong>{stateHead}</strong> : null}
              {statusText ? (
                <span className="pane-popover__statustext">
                  {stateHead ? " · " : ""}
                  {statusText}
                </span>
              ) : null}
            </span>
          ) : null}
          {aside ? <span className="pane-popover__aside">{aside}</span> : null}
          <button
            type="button"
            className="pane-icon-btn pane-popover__close"
            onClick={onClose}
            aria-label={shellText("close", lang)}
            title={shellText("closeEsc", lang)}
          >
            ×
          </button>
        </div>
      )}
      <HelpArea className="pane-popover__help">
        <div className={`pane-popover__body${plain ? " is-plain" : ""}`}>{children}</div>
      </HelpArea>
    </div>
  );
}
